import { useMemo, type ReactNode } from 'react'

import { NativeBaseProvider } from 'native-base'

import { createNativeBaseTheme, type NativeBaseExtras } from '../theme/adapters/nativebase'
import { createTheme } from '../theme/createTheme'
import type { Theme } from '../theme/types'

export interface UIProviderProps {
  /** A resolved theme from `createTheme()`. Defaults to the unified tokens. */
  theme?: Theme
  /**
   * App-specific NativeBase configuration (component overrides, fontConfig,
   * legacy color groups). Temporary seam until Phase 3 component extraction.
   */
  nativeBase?: NativeBaseExtras
  children: ReactNode
}

/**
 * Single theme provider for both apps. Mount it by *replacing* the app's
 * `NativeBaseProvider` — never nest the two, or components resolve tokens
 * from duplicate contexts.
 */
export function UIProvider({ theme, nativeBase, children }: UIProviderProps) {
  const resolvedTheme = useMemo(() => createNativeBaseTheme(theme ?? createTheme(), nativeBase), [theme, nativeBase])
  return <NativeBaseProvider theme={resolvedTheme}>{children}</NativeBaseProvider>
}

/**
 * Contract name from CBP-3939.
 *
 * This provider does NOT currently wrap Gluestack UI. The packages the ticket
 * named (`@gluestack-ui/themed`, `@gluestack-ui/config`) are the dormant v1
 * runtime, and Gluestack v5 dropped non-Metro web bundlers while CWA is on
 * Vite — so Decision D6 (primitive layer) is deferred to CBP-3942 pending a
 * feasibility spike. Today this renders `NativeBaseProvider` via the
 * `nativebase` adapter; swapping the primitive layer later is a single-file
 * change in `theme/adapters/` with no call-site churn.
 */
export const GluestackProvider = UIProvider
